import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { Modal, Box, Divider, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
    ControlledTextField,
    ControlledCheckbox,
    ControlledSingleSelect,
    SimpleButton,
    ErrorMessage,
} from '@/src/ui';
import { Constants } from '@/src/utils';
import type { DistrictBaseDTO } from '../models/districtDTOs';

interface CreateEditDistrictModalProps {
    open: boolean;
    mode: 'create' | 'edit';
    district?: DistrictBaseDTO | null;
    onClose: () => void;
    error?: string;
    setError?: (msg: string) => void;
    onSubmitCallback: () => void;
}

import { apiRequest } from '@/src/services';

const schema = yup.object().shape({
    name: yup
        .string()
        .required('District Name is required')
        .max(
            Constants.MAX_CHARS.DISTRICT_FIELDS,
            `District Name cannot exceed ${Constants.MAX_CHARS.DISTRICT_FIELDS} characters`
        ),
    code: yup
        .string()
        .required('Code is required')
        .max(
            Constants.MAX_CHARS.DISTRICT_FIELDS,
            `Code cannot exceed ${Constants.MAX_CHARS.DISTRICT_FIELDS} characters`
        ),
    stateCode: yup
        .string()
        .required('State Code is required')
        .max(
            Constants.MAX_CHARS.DISTRICT_STATE_CODE,
            `State Code cannot exceed ${Constants.MAX_CHARS.DISTRICT_STATE_CODE} characters`
        ),
    ssoDomain: yup
        .string()
        .required('SSO Domain is required')
        .max(
            Constants.MAX_CHARS.DISTRICT_FIELDS,
            `SSO Domain cannot exceed ${Constants.MAX_CHARS.DISTRICT_FIELDS} characters`
        ),
    ssoProviderMasterId: yup
        .number()
        .typeError('SSO Provider Master Id must be a number')
        .integer()
        .min(0)
        .required('SSO Provider Master is required'),
    status: yup
        .number()
        .typeError('Status must be a number')
        .integer()
        .min(0)
        .required('Status is required'),
    accessEndDUTC: yup.date().nullable(),
    isQADistrict: yup.boolean().required(),
    isPracticeAllowed: yup.boolean().required(),
    isPracticeChatAllowed: yup.boolean().required(),
});

const sanitizeDate = (value: any) => {
    if (!value) return value;
    const date = value instanceof Date ? new Date(value) : new Date(value.toString());
    date.setSeconds(0, 0);
    return date;
};

const statusOptions = [
    { id: 1, name: 'Active' },
    { id: 2, name: 'Inactive' },
    { id: 3, name: 'Deleted' },
];

const CreateEditDistrictModal: React.FC<CreateEditDistrictModalProps> = ({
    open,
    mode,
    district,
    onClose,
    onSubmitCallback,
}) => {
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const {
        control,
        setValue,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: district?.name || '',
            code: district?.code || '',
            stateCode: district?.stateCode || '',
            ssoDomain: district?.ssoDomain || '',
            ssoProviderMasterId: district?.ssoProviderMasterId ?? 0,
            status: mode === 'create' ? 1 : (district?.status ?? 0),
            accessEndDUTC: null,
            isQADistrict: district?.isQADistrict ?? false,
            isPracticeAllowed: district?.isPracticeAllowed ?? false,
            isPracticeChatAllowed: district?.isPracticeChatAllowed ?? false,
        },
    });

    useEffect(() => {
        if (open) {
            reset({
                name: district?.name || '',
                code: district?.code || '',
                stateCode: district?.stateCode || '',
                ssoDomain: district?.ssoDomain || '',
                ssoProviderMasterId: district?.ssoProviderMasterId ?? 0,
                status: mode === 'create' ? 1 : (district?.status ?? 0),
                accessEndDUTC: district?.accessEndDUTC ? new Date(district.accessEndDUTC) : null,
                isQADistrict: district?.isQADistrict ?? false,
                isPracticeAllowed: district?.isPracticeAllowed ?? false,
                isPracticeChatAllowed: district?.isPracticeChatAllowed ?? false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, district, reset]);

    const onSubmit = async () => {
        if (submitting || success) return;

        setSubmitting(true);
        setError('');

        const values = watch();
        try {
            if (mode === 'edit') {
                const payload: DistrictBaseDTO = {
                    ...values,
                    accessEndDUTC: values.accessEndDUTC || null,
                    id: district?.id,
                };
                await apiRequest('PUT', `dbo/districts/${district?.id}`, payload);
            } else {
                await apiRequest('POST', 'dbo/districts', values);
            }
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                onSubmitCallback();
            }, 750);
        } catch {
            setError('Failed to save district');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    p: 4,
                    background: 'white',
                    borderRadius: 2,
                    maxWidth: 700,
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {mode === 'edit' ? 'Edit District' : 'Add District'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <ControlledTextField
                            label="District Name"
                            name="name"
                            error={errors.name}
                            control={control}
                            maxLength={Constants.MAX_CHARS.DISTRICT_FIELDS}
                        />
                        <ControlledTextField
                            label="Code"
                            name="code"
                            error={errors.code}
                            control={control}
                            maxLength={Constants.MAX_CHARS.DISTRICT_FIELDS}
                        />
                        <ControlledTextField
                            label="State Code"
                            name="stateCode"
                            error={errors.stateCode}
                            control={control}
                            maxLength={Constants.MAX_CHARS.DISTRICT_STATE_CODE}
                        />
                        <ControlledTextField
                            label="SSO Domain"
                            name="ssoDomain"
                            error={errors.ssoDomain}
                            control={control}
                            maxLength={Constants.MAX_CHARS.DISTRICT_FIELDS}
                        />
                        <ControlledTextField
                            label="SSO Provider Master Id"
                            name="ssoProviderMasterId"
                            type="number"
                            error={errors.ssoProviderMasterId}
                            control={control}
                        />
                        {mode === 'edit' && (
                            <ControlledSingleSelect
                                name="status"
                                label="Status"
                                options={statusOptions}
                                control={control}
                                onChange={(e) => setValue('status', Number(e.target.value))}
                                sx={{ width: '100%' }}
                                error={errors.status}
                            />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Controller
                                name="accessEndDUTC"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DatePicker
                                        {...field}
                                        label="Access End Date"
                                        onChange={(newValue) =>
                                            field.onChange(sanitizeDate(newValue))
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!fieldState.error,
                                                helperText: fieldState.error?.message,
                                                fullWidth: true,
                                                margin: 'normal',
                                            },
                                        }}
                                    />
                                )}
                            />
                            <SimpleButton
                                color="error"
                                onClick={() => setValue('accessEndDUTC', null)}
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                Remove
                            </SimpleButton>
                        </Box>
                        <Box
                            sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2, flexWrap: 'wrap' }}
                        >
                            <ControlledCheckbox
                                name="isQADistrict"
                                label="QA District"
                                control={control}
                                color="primary"
                            />
                            <ControlledCheckbox
                                name="isPracticeAllowed"
                                label="Practice Allowed"
                                control={control}
                                color="primary"
                            />
                            <ControlledCheckbox
                                name="isPracticeChatAllowed"
                                label="Practice Chat Allowed"
                                control={control}
                                color="primary"
                            />
                        </Box>
                    </Box>
                    {error ? (
                        <Box sx={{ mt: 2 }}>
                            <ErrorMessage error={error} />
                        </Box>
                    ) : null}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                        <SimpleButton type="submit">Save</SimpleButton>
                        <SimpleButton onClick={onClose} color="error" type="button">
                            Cancel
                        </SimpleButton>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default CreateEditDistrictModal;
